/**
 * Pure-self-built browser error reporter.
 *
 * Wires `window.onerror` + `window.onunhandledrejection` to a fire-and-forget
 * POST to `/api/v1/telemetry/client-error`. NOT a Sentry replacement — we
 * deliberately keep the surface narrow:
 *   - one report per (stack-fingerprint) per 5 minutes (sessionStorage TTL)
 *   - never throws into the user's flow (every fetch failure is swallowed)
 *   - skips React-internal errors (the ErrorBoundary calls reportClientError
 *     directly with cleaner context)
 *
 * Volume rationale: a real bug fires once per user session at most; an
 * accidental loop (e.g. setState-during-render) can fire thousands of times
 * per second — the dedupe keeps both the network and our DB safe. We pair
 * this with a 10/min server-side rate limit per IP for belt-and-braces.
 */

interface ReporterOptions {
  /** Base URL of the API. Telemetry path is appended. */
  apiUrl: string
  /** Build version string (e.g. git SHA, package.json version). */
  releaseVersion: string
}

interface ReportPayload {
  route?: string
  error_message: string
  error_stack?: string
  user_agent?: string
  viewport_w?: number
  viewport_h?: number
  released_version: string
  session_id?: string
}

/** Worker-shaped payload (api.ohear.ai/_errors). Distinct from the legacy
 *  /api/v1/telemetry/client-error shape so we can target the new dedicated
 *  error-collector Worker without disturbing existing callers. */
interface WorkerReportPayload {
  url: string
  ua: string
  message: string
  stack?: string
  type: 'unhandledrejection' | 'error' | 'react_error_boundary' | 'manual'
  context?: Record<string, unknown>
  sessionId: string
  userId?: string
  orgId?: string
}

let workerEndpoint: string | null = null
let workerContextProvider: (() => Record<string, unknown>) | undefined

const DEDUPE_TTL_MS = 5 * 60 * 1000
const STORAGE_PREFIX = 'ohear:err:'
const SESSION_KEY = 'ohear:err:session'
const TELEMETRY_PATH = '/api/v1/telemetry/client-error'
const VERSION_OVERRIDE = import.meta.env.VITE_CF_WORKER_VERSION_OVERRIDE as string | undefined

/** djb2 — small, fast, non-cryptographic. Fine for fingerprinting. */
function fingerprint(s: string): string {
  let h = 5381
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0
  }
  // Hex, unsigned.
  return (h >>> 0).toString(16)
}

function getOrCreateSessionId(): string {
  if (typeof sessionStorage === 'undefined') return 'no-session'
  try {
    const existing = sessionStorage.getItem(SESSION_KEY)
    if (existing) return existing
    const id = `s_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`
    sessionStorage.setItem(SESSION_KEY, id)
    return id
  } catch {
    return 'no-session'
  }
}

function shouldSend(stack: string): boolean {
  if (typeof sessionStorage === 'undefined') return true
  const fp = fingerprint(stack)
  const key = STORAGE_PREFIX + fp
  try {
    const last = sessionStorage.getItem(key)
    const now = Date.now()
    if (last) {
      const ts = Number.parseInt(last, 10)
      if (Number.isFinite(ts) && now - ts < DEDUPE_TTL_MS) return false
    }
    sessionStorage.setItem(key, String(now))
    return true
  } catch {
    // If storage is full / disabled, default to sending — better a noisy
    // error than a silent regression.
    return true
  }
}

let installed = false
let cachedOpts: ReporterOptions | null = null

/**
 * Send one report. Exported so the ErrorBoundary can call it directly with
 * its own message + stack rather than relying on the global handlers.
 *
 * Always fire-and-forget; never throws. Returns true if a report was
 * dispatched (i.e. not deduped and not silently dropped).
 */
export function reportClientError(input: {
  message: string
  stack?: string
  source?: 'window.onerror' | 'unhandledrejection' | 'errorBoundary' | 'manual'
}): boolean {
  if (!cachedOpts) return false
  const message = (input.message || '').slice(0, 2000)
  if (!message) return false

  const stack = input.stack ? input.stack.slice(0, 20_000) : undefined
  // Fingerprint on stack when we have one (most stable), else message.
  if (!shouldSend(stack || message)) return false

  const payload: ReportPayload = {
    route: typeof location !== 'undefined' ? location.pathname + location.search : undefined,
    error_message: message,
    error_stack: stack,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : undefined,
    viewport_w: typeof window !== 'undefined' ? window.innerWidth : undefined,
    viewport_h: typeof window !== 'undefined' ? window.innerHeight : undefined,
    released_version: cachedOpts.releaseVersion,
    session_id: getOrCreateSessionId(),
  }

  const url = `${cachedOpts.apiUrl}${TELEMETRY_PATH}`
  const body = JSON.stringify(payload)

  try {
    // sendBeacon is best-effort, fire-and-forget, survives unload — perfect
    // for an exception during navigation.
    if (
      !VERSION_OVERRIDE &&
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const ok = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
      if (ok) return true
      // Fall through to fetch if beacon refused (e.g. queue full).
    }
    void fetch(url, {
      method: 'POST',
      body,
      keepalive: true,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(VERSION_OVERRIDE
          ? { 'Cloudflare-Workers-Version-Overrides': `ohear-api="${VERSION_OVERRIDE}"` }
          : {}),
      },
    }).catch(() => {
      // Reporter must never break the app. Telemetry is best-effort.
    })
    return true
  } catch {
    return false
  }
}

function isReactInternalError(msg: string): boolean {
  // Errors caught by the ErrorBoundary surface here too (they re-throw inside
  // React's reconciler). We let the ErrorBoundary drive its own report so we
  // get the React component stack instead of an opaque "Minified React error".
  // Heuristic: well-known React error prefixes.
  return (
    msg.includes('Minified React error') ||
    msg.includes('React will try to recreate this component tree')
  )
}

/**
 * Install global handlers. Idempotent — safe to call from main.tsx during
 * StrictMode double-render.
 */
export function installErrorReporter(opts: ReporterOptions): void {
  cachedOpts = opts
  if (installed || typeof window === 'undefined') return
  installed = true

  window.addEventListener('error', (event: ErrorEvent) => {
    const msg = event.message || event.error?.message || 'Unknown error'
    if (isReactInternalError(msg)) return
    reportClientError({
      message: msg,
      stack: event.error?.stack,
      source: 'window.onerror',
    })
  })

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason
    let message = 'Unhandled promise rejection'
    let stack: string | undefined
    if (reason instanceof Error) {
      message = reason.message || message
      stack = reason.stack
    } else if (typeof reason === 'string') {
      message = reason
    } else if (reason && typeof reason === 'object') {
      try {
        message = JSON.stringify(reason).slice(0, 2000)
      } catch {
        // unserializable — keep default message.
      }
    }
    if (isReactInternalError(message)) return
    reportClientError({
      message,
      stack,
      source: 'unhandledrejection',
    })
  })
}

/** Test-only escape hatch — clears the dedupe + session id. */
export function _resetReporterForTests(): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    const keys: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i)
      if (k && k.startsWith(STORAGE_PREFIX)) keys.push(k)
    }
    keys.forEach((k) => sessionStorage.removeItem(k))
    sessionStorage.removeItem(SESSION_KEY)
    sessionStorage.removeItem(WORKER_SPOOL_KEY)
  } catch {
    // ignored
  }
  installed = false
  cachedOpts = null
  workerEndpoint = null
  workerContextProvider = undefined
  workerRecentlyReported.clear()
}

// ─────────────────────────────────────────────────────────────────────────────
// New Worker-targeted reporter (api.ohear.ai/_errors).
//
// The legacy `installErrorReporter` / `reportClientError` pair POSTs to the
// API server's /api/v1/telemetry/client-error route. The functions below
// target the dedicated Cloudflare error-collector Worker which writes both
// to Postgres and Workers Analytics Engine. They share nothing but listener
// installation — by design, so existing callers keep working unchanged.
// ─────────────────────────────────────────────────────────────────────────────

const WORKER_DEDUPE_WINDOW_MS = 5_000
const WORKER_SPOOL_KEY = 'ohear-error-worker-spool'
const WORKER_MAX_SPOOL = 20
const WORKER_ALLOWED_TYPES = new Set([
  'unhandledrejection',
  'error',
  'react_error_boundary',
  'manual',
])
const workerRecentlyReported = new Map<string, number>()
let workerListenersInstalled = false

/**
 * Install the Worker-targeted reporter. Idempotent — the listeners only attach
 * once across multiple `init()` calls. Spool is replayed on init and on
 * `online` events so reports survive a brief disconnect.
 */
export function init(endpoint: string, getContext?: () => Record<string, unknown>): void {
  workerEndpoint = endpoint
  workerContextProvider = getContext

  if (!workerListenersInstalled && typeof window !== 'undefined') {
    window.addEventListener('error', onWorkerWindowError)
    window.addEventListener('unhandledrejection', onWorkerUnhandledRejection)
    window.addEventListener('online', () => {
      void flushWorkerSpool()
    })
    workerListenersInstalled = true
  }

  void flushWorkerSpool()
}

/**
 * Report a single error to the Worker. Use this from React error boundaries
 * and from any code-side catch where you want a structured trail.
 */
export function report(error: unknown, type = 'manual', extra?: Record<string, unknown>): void {
  const safeType = (WORKER_ALLOWED_TYPES.has(type) ? type : 'manual') as WorkerReportPayload['type']
  const { message, stack } = normalizeWorkerError(error)
  emitWorker(message, stack, safeType, extra)
}

function onWorkerWindowError(ev: ErrorEvent): void {
  const { message, stack } = normalizeWorkerError(ev.error ?? ev.message)
  emitWorker(message || ev.message || 'unknown error', stack, 'error', {
    filename: ev.filename,
    lineno: ev.lineno,
    colno: ev.colno,
  })
}

function onWorkerUnhandledRejection(ev: PromiseRejectionEvent): void {
  const { message, stack } = normalizeWorkerError(ev.reason)
  emitWorker(message || 'unhandled promise rejection', stack, 'unhandledrejection')
}

function normalizeWorkerError(input: unknown): { message: string; stack?: string } {
  if (input instanceof Error) {
    return { message: input.message, stack: input.stack }
  }
  if (typeof input === 'string') return { message: input }
  try {
    return { message: JSON.stringify(input) }
  } catch {
    return { message: String(input) }
  }
}

function emitWorker(
  message: string,
  stack: string | undefined,
  type: WorkerReportPayload['type'],
  extra?: Record<string, unknown>,
): void {
  if (!workerEndpoint || typeof window === 'undefined') return

  const dedupeKey = `${message}::${stack ?? ''}`
  const now = Date.now()
  for (const [k, ts] of workerRecentlyReported) {
    if (now - ts >= WORKER_DEDUPE_WINDOW_MS) workerRecentlyReported.delete(k)
  }
  const last = workerRecentlyReported.get(dedupeKey)
  if (last && now - last < WORKER_DEDUPE_WINDOW_MS) return
  workerRecentlyReported.set(dedupeKey, now)

  const baseContext = workerContextProvider?.() ?? {}
  const userId = pickWorkerString(baseContext.userId)
  const orgId = pickWorkerString(baseContext.orgId)

  const payload: WorkerReportPayload = {
    url: window.location.href,
    ua: navigator.userAgent,
    message,
    stack,
    type,
    context: { ...baseContext, ...(extra ?? {}) },
    sessionId: getOrCreateSessionId(),
  }
  if (userId) payload.userId = userId
  if (orgId) payload.orgId = orgId

  void sendToWorker(payload)
}

function pickWorkerString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}

async function sendToWorker(payload: WorkerReportPayload): Promise<void> {
  if (!workerEndpoint) return
  const body = JSON.stringify(payload)

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([body], { type: 'application/json' })
      if (navigator.sendBeacon(workerEndpoint, blob)) return
    } catch {
      // fall through
    }
  }

  try {
    const res = await fetch(workerEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
      mode: 'cors',
    })
    if (!res.ok) spoolWorker(payload)
  } catch {
    spoolWorker(payload)
  }
}

function spoolWorker(payload: WorkerReportPayload): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    const raw = sessionStorage.getItem(WORKER_SPOOL_KEY)
    const arr: WorkerReportPayload[] = raw ? (JSON.parse(raw) as WorkerReportPayload[]) : []
    arr.push(payload)
    while (arr.length > WORKER_MAX_SPOOL) arr.shift()
    sessionStorage.setItem(WORKER_SPOOL_KEY, JSON.stringify(arr))
  } catch {
    // sessionStorage full / disabled — drop on the floor.
  }
}

async function flushWorkerSpool(): Promise<void> {
  if (typeof sessionStorage === 'undefined' || !workerEndpoint) return
  let arr: WorkerReportPayload[] = []
  try {
    const raw = sessionStorage.getItem(WORKER_SPOOL_KEY)
    if (!raw) return
    arr = JSON.parse(raw) as WorkerReportPayload[]
    sessionStorage.removeItem(WORKER_SPOOL_KEY)
  } catch {
    return
  }
  for (const payload of arr) {
    await sendToWorker(payload)
  }
}
